import UIkit from "uikit";
import { clearStaleUIKitModalOverlays } from "./workOrderAddPanel";

export const isRecurringWorkOrder = (wo) =>
  Boolean(wo?.isVirtual || wo?.task_recurrence_rule_id || wo?.task_source === "recurrence");

export const RECURRING_SCOPE_LABELS = {
  this: "Only this event",
  future: "This and all future events",
  all: "All events",
  allNote:
    "Applies to upcoming, not-yet-started tasks. Past and in-progress tasks are unchanged.",
  metaDelete: "This is a recurring task. Choose what to delete:",
  metaApply: "This is a recurring task. Choose what to apply:",
};

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Prompt for single-occurrence vs all-future scope on recurring task edits. */
export function promptRecurringEditScope({
  itemLabel = "Task",
  summary,
  intent = "edit",
  title,
  confirmLabel,
  metaText = RECURRING_SCOPE_LABELS.metaApply,
} = {}) {
  const modalTitle =
    title ||
    (intent === "move" ? `Move ${itemLabel}` : `Edit ${itemLabel}`);
  const confirmText =
    confirmLabel ||
    (intent === "move" ? "Move" : intent === "open" ? "Continue" : "Save");
  const summaryHtml = summary
    ? `<b>${escapeHtml(summary)}</b>`
    : "this recurring task";
  const leadText =
    intent === "move"
      ? `Move ${summaryHtml} to a new date.`
      : intent === "open"
        ? `You are editing ${summaryHtml}.`
        : `Save changes to ${summaryHtml}?`;

  return new Promise((resolve) => {
    let resolved = false;
    let pendingValue;
    const scopeName = `rbr-recurring-edit-scope-${Date.now()}`;

    const requestClose = (value) => {
      pendingValue = value;
      try {
        dialog.hide();
      } catch (_) {
        if (!resolved) {
          resolved = true;
          clearStaleUIKitModalOverlays();
          resolve(pendingValue);
        }
      }
    };

    // UIkit.modal.dialog already wraps content in .uk-modal > .uk-modal-dialog.
    // Do not nest another .uk-modal-dialog here — inner dialogs stay opacity:0.
    const dialog = UIkit.modal.dialog(
      `
      <div class="uk-modal-body">
        <button class="uk-modal-close-default" type="button" uk-close></button>
        <h2 class="uk-modal-title">${escapeHtml(modalTitle)}</h2>
        <p>${leadText}</p>
        <div class="uk-margin">
          <p class="uk-text-meta uk-margin-small-bottom">${escapeHtml(metaText)}</p>
          <label class="uk-display-block uk-margin-small">
            <input
              class="uk-radio uk-margin-small-right"
              type="radio"
              name="${scopeName}"
              value="this"
              checked
            />
            ${RECURRING_SCOPE_LABELS.this}
          </label>
          <label class="uk-display-block uk-margin-small">
            <input
              class="uk-radio uk-margin-small-right"
              type="radio"
              name="${scopeName}"
              value="future"
            />
            ${RECURRING_SCOPE_LABELS.future}
          </label>
          <label class="uk-display-block">
            <input
              class="uk-radio uk-margin-small-right"
              type="radio"
              name="${scopeName}"
              value="all"
            />
            ${RECURRING_SCOPE_LABELS.all}
          </label>
          <p class="uk-text-meta uk-margin-small-top">${RECURRING_SCOPE_LABELS.allNote}</p>
        </div>
        <p class="uk-text-right">
          <button
            class="uk-button hms-btn uk-modal-delete-cancel"
            type="button"
            data-rbr-scope="cancel"
          >
            Cancel
          </button>
          <button
            class="uk-button hms-btn hms-gray-btn"
            type="button"
            data-rbr-scope="confirm"
          >
            ${escapeHtml(confirmText)}
          </button>
        </p>
      </div>
    `,
      { bgClose: false, escClose: true }
    );

    const el = dialog.$el;
    const getScope = () => {
      const checked = el.querySelector(`input[name="${scopeName}"]:checked`);
      const value = checked?.value;
      if (value === "future" || value === "all") return value;
      return "this";
    };

    const finalize = () => {
      if (resolved) return;
      resolved = true;
      clearStaleUIKitModalOverlays();
      resolve(pendingValue);
    };

    UIkit.util.on(el, "hidden", finalize);

    el.querySelector('[data-rbr-scope="cancel"]').addEventListener("click", () =>
      requestClose(null)
    );
    el.querySelector('[data-rbr-scope="confirm"]').addEventListener("click", () =>
      requestClose(getScope())
    );
  });
}

/** Day-of-week (0=Sun..6=Sat) from a YYYY-MM-DD string, UTC-safe. */
export function weekdayFromDate(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).getUTCDay();
}

/**
 * When dragging one weekly slot to a new weekday, remap only that slot.
 * e.g. weekdays [Thu, Sat] dragged Thu→Fri → [Fri, Sat], not [Fri] only.
 */
export function remapWeeklyWeekdays(weekdays, fromDate, toDate) {
  const fromDow = weekdayFromDate(fromDate);
  const toDow = weekdayFromDate(toDate);
  const existing = [...new Set((weekdays || []).map(Number))].sort((a, b) => a - b);
  if (!existing.length) return [toDow];
  return [...new Set(existing.map((d) => (d === fromDow ? toDow : d)))].sort(
    (a, b) => a - b
  );
}

export function buildDragFutureSplitPayload(rule, fromDate, droppedDate) {
  const payload = {};
  if (!rule) return payload;

  if (rule.frequency === "weekly") {
    payload.weekdays = remapWeeklyWeekdays(rule.weekdays, fromDate, droppedDate);
  } else if (["monthly", "quarterly", "yearly"].includes(rule.frequency)) {
    const fromDay = parseInt(String(fromDate).slice(8, 10), 10);
    const toDay = parseInt(String(droppedDate).slice(8, 10), 10);
    if (!rule.month_day || Number(rule.month_day) === fromDay) {
      payload.month_day = toDay;
    }
  }
  return payload;
}
